import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Stack, Box, IconButton, Link, TextField,
} from '@mui/material';
import dayjs from 'dayjs';
import DataTable from '../components/DataTable';
import CreatePoDialog from '../components/CreatePoDialog';
import CreateSkusDialog from '../components/CreateSkusDialog';
import sendRequest from '../utils/sendRequest';
import { RECEIVED_STATUSES, STATUSES } from '../utils/constants';
import GoogleSheetsIcon from '../icons/google-sheet-icon.png';
import ClearIcon from '@mui/icons-material/Clear';
import ActionRowButtons from "../components/ActionRowButtons";
import DevActionRowButtons from "../components/DevActionRowButtons";
import LargeTooltip from '../styled/CustomTooltip';
import PoFilters from '../components/PoFilters';
import FlagIcon from '@mui/icons-material/Flag';
import { STATUS_ICONS } from '../utils/statusIcons';
import WorksheetAutomationsIcon from "../components/WorksheetAutomations";

const PurchaseOrders = ({ addSnackbar, setLoading }) => {
  const [poRows, setPoRows] = useState([]);
  const [hasLuxRole, setHasLuxRole] = useState(false);
  const [hasOakRole, setHasOakRole] = useState(false);
  const [hasEssxRole, setHasEssxRole] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [filteredPoRows, setFilteredPoRows] = useState([]);

  const [devAction, setDevAction] = useState(false);
  const [createPoAllowed, setCreatePoAllowed] = useState(false);
  const [createSkusAllowed, setCreateSkusAllowed] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);

  const [statusFilter, setStatusFilter] = useState([]);
  const [assignedToFilter, setAssignedToFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [daysMoreThan, setDaysMoreThan] = useState('');
  const [daysLessThan, setDaysLessThan] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);
  
    return () => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    let filtered = [...poRows];
  
    const lower = debouncedSearchText.toLowerCase();
    if (lower) {
      filtered = filtered.filter(row =>
        row.name.toLowerCase().includes(lower) ||
        row.status.toLowerCase().includes(lower) ||
        row.id.toString().includes(lower) ||
        (row.po_id?.toString().includes(lower))
      );
    }
  
    // Date Filters
    if (dateRangeStart || dateRangeEnd) {
      filtered = filtered.filter(row => {
        const rowDate = dayjs(row.date_created);
        const startOK = dateRangeStart ? rowDate.isSame(dayjs(dateRangeStart), 'day') || rowDate.isAfter(dayjs(dateRangeStart), 'day') : true;
        const endOK = dateRangeEnd ? rowDate.isSame(dayjs(dateRangeEnd), 'day') || rowDate.isBefore(dayjs(dateRangeEnd), 'day') : true;
        return startOK && endOK;
      });
    }
  
    if (statusFilter.length) {
      filtered = filtered.filter(row => statusFilter.includes(row.status));
    }
  
    if (assignedToFilter.length) {
      filtered = filtered.filter(row => assignedToFilter.includes(row.assigned_to.role));
    }

    if (typeFilter.length) {
      filtered = filtered.filter(row => typeFilter.includes(row.type));
    }
  
    if (daysMoreThan !== '') {
      filtered = filtered.filter(row => row.days_passed !== null && row.days_passed > Number(daysMoreThan));
    }
    if (daysLessThan !== '') {
      filtered = filtered.filter(row => row.days_passed !== null && row.days_passed < Number(daysLessThan));
    }

    if (flaggedOnly) {
      filtered = filtered.filter(row => row.is_flagged);
    }

    if (!showArchived) {
      filtered = filtered.filter(row => !row.archived);
    }
  
    setFilteredPoRows(filtered);
  }, [
    debouncedSearchText,
    poRows,
    dateRangeStart,
    dateRangeEnd,
    statusFilter,
    assignedToFilter,
    daysMoreThan,
    daysLessThan,
    flaggedOnly,
    showArchived,
    typeFilter
  ]);

  const fetchPos = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      let data = await sendRequest('purchase-orders');
      const currentDate = new Date();
      const parsedData = data.po_list.map(row => {
        const isCompleted = row.assigned_to.role === 'Completed';
        const isPendingDelivery = row.status === STATUSES.pendingDelivery;
        const delivDate = row.expected_delivery_date;
        const daysPassed = isCompleted ? null : isPendingDelivery ? (
          Math.floor((currentDate - (delivDate ? new Date(delivDate) : currentDate)) / (24 * 60 * 60 * 1000))
        ) : Math.floor(
          (currentDate - new Date(row.last_action_date)) / (24 * 60 * 60 * 1000)
        );
        return ({
          ...row,
          date_created: new Date(row.date_created),
          days_passed: daysPassed,
        })
      });
      parsedData.forEach(row => {
        if (row.archived) {
          row.status = 'Archived';
          row.assigned_to = { role: 'Archived', user_role: false };
        }
      })
      setCreatePoAllowed(data.create_po_allowed);
      setDevAction(data.dev_allowed);
      setShowActions(parsedData.some(row => row.actions.length));
      setPoRows(parsedData);
      setHasLuxRole(data.roles['lux']);
      setHasOakRole(data.roles['oak']);
      setHasEssxRole(data.roles['essx']);
      setCreateSkusAllowed(data.create_skus_allowed);
    } catch (error) {
      if (!background) addSnackbar(error.message, false);
    } finally {
      if (!background) setLoading(false);
    }
  }, [addSnackbar, setLoading]);

  useEffect(() => {
    fetchPos();
    const intervalId = setInterval(() => {
      if (!document.hidden) {
        fetchPos(true);
      }
    }, 15000);
    return () => clearInterval(intervalId);
  }, [fetchPos]);

  const PoHeader = useMemo(() => {
    return (
      <Stack
        direction='row'
        sx={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        spacing={1}
      >
        <TextField
          size="small"
          placeholder="Search ID's, Names, Statuses, Sellercloud ID's..."
          value={searchText}
          autoComplete='off'
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ width: 500 }}
          InputProps={{
            endAdornment: searchText && (
              <IconButton
                size="small"
                onClick={() => setSearchText('')}
                edge="end"
                aria-label="clear search"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            ),
          }}
        />
        <Stack direction='row' spacing={1} alignItems="center">
          { createSkusAllowed && (
            <CreateSkusDialog
              addSnackbar={addSnackbar}
            />
          )}
          { createPoAllowed && (
            <CreatePoDialog
              addSnackbar={addSnackbar}
              fetchPos={fetchPos}
              lux={hasLuxRole}
              oak={hasOakRole}
              essx={hasEssxRole}
            />
          )}
        </Stack>
      </Stack>
    )
  }, [
    fetchPos,
    addSnackbar,
    createPoAllowed,
    searchText,
    setSearchText,
    hasLuxRole,
    hasOakRole,
    hasEssxRole,
    createSkusAllowed,
  ]);

  const poColumns = useMemo(() => {
    const highlightMatch = (text, search, key = 1) => {
      if (!search) return <span key={key}>{text}</span>;
      const index = text.toLowerCase().indexOf(search.toLowerCase());
      if (index === -1) return <span key={key}>{text}</span>;
    
      return (
        <span key={key}>
          {text.substring(0, index)}
          <span style={{ backgroundColor: 'yellow' }}>
            {text.substring(index, index + searchText.length)}
          </span>
          {text.substring(index + searchText.length)}
        </span>
      );
    };

    return [
      { field: 'id', type: 'number', headerName: 'ID', headerAlign: 'center', align: 'center', width: 75, sortable: true,
        renderCell: (params) => highlightMatch(params.value.toString(), searchText)
      },
      { field: 'type', headerName: 'Type', width: 100, headerAlign: 'center', align: 'center' },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 200,
        renderCell: (params) => (
          <Box display="flex" alignItems="center" gap={0.5}>
            {params.row.is_flagged && (
              <LargeTooltip title="Comments Page" arrow>
                <Link
                  href={`/purchase-orders/${params.row.id}/comments`}
                  underline="none"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <FlagIcon fontSize="small" sx={{ color: 'error.main' }} />
                </Link>
              </LargeTooltip>
            )}
            <LargeTooltip title="Detail Page" arrow>
              <Link
                href={`/purchase-orders/${params.row.id}`}
                rel="noopener noreferrer"
                underline="hover"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {highlightMatch(params.value, searchText)}
              </Link>
            </LargeTooltip>
          </Box>
        )
      },
      { field: 'date_created', type: 'date', headerName: 'Date Created', width: 100, headerAlign: 'center', align: 'center' },
      { field: 'status', headerName: 'Status', width: 275,
        renderCell: (params) => {
          const value = params.value;
          const hasDash = value.includes("-");
          const isProcessing = value.includes("...");
          const parts = hasDash
              ? value.split("-").map(part => part.trim())
              : [value]; // Always 2 lines: [top, bottom]
          const statusIcon = STATUS_ICONS[isProcessing ? 'pending' : value];

          return (
              <Box
                  sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      height: '100%',
                  }}
              >
                  <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      minWidth: 32,
                  }}>
                      {statusIcon}
                  </Box>
                  <Box
                      sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          height: '100%',
                          minWidth: 0,
                          ml: 1,
                      }}
                  >
                    {hasDash ? (
                      <>
                        <Box sx={{ height: '40%', minHeight: '40%', display: 'flex', alignItems: 'center' }}>
                          {highlightMatch(parts[0], searchText, 0)}
                        </Box>
                        <Box sx={{ height: '40%', minHeight: '40%', display: 'flex', alignItems: 'center' }}>
                          {highlightMatch(parts[1], searchText, 1)}
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {highlightMatch(parts[0], searchText, 0)}
                      </Box>
                    )}
                  </Box>
              </Box>
          );
        }
      },
      showActions && { field: 'actions', headerName: 'Actions', width: 180, sortable: false, headerAlign: 'center',
        renderCell: (params) => {
          if (params.value.length === 0) {
            return null;
          }
          return (
            <ActionRowButtons
              row={params.row} addSnackbar={addSnackbar} fetchPos={fetchPos}
            />
          )
        }
      },
      { field: 'spreadsheet_id', headerName: 'File', headerAlign: 'center', align: 'center', width: 80, sortable: false, renderCell: (params) => {
          if (!params.value) {
            return null;
          }
          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <LargeTooltip title="Open Spreadsheet" arrow>
                <IconButton
                  component="a"
                  href={'https://docs.google.com/spreadsheets/d/' + params.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="open google sheet"
                >
                  <img
                    src={GoogleSheetsIcon}
                    alt="Google Sheets"
                    style={{ width: '24px', height: '24px' }}
                  />
                </IconButton>
              </LargeTooltip>
              {params.row.actions.includes("worksheet_automations") && (
                <WorksheetAutomationsIcon
                  po_doc_id={params.row.doc_id}
                  addSnackbar={addSnackbar}
                />
              )}
            </Stack>
          );
        }
      },
      { field: 'po_id', headerName: 'Sellercloud PO', width: 125, headerAlign: 'center', align: 'center', sortable: false, renderCell: (params) => {
          if (!params.value) {
            return null;
          }
          return (
            <LargeTooltip title="Open PO in Sellercloud" arrow>
              <Link
                href={`https://lux.delta.sellercloud.com/purchasing/po-details.aspx?id=${params.value}`}
                rel="noopener noreferrer"
                underline='hover'
                target='_blank'
              >
                {highlightMatch(params.value.toString(), searchText)}
              </Link>
            </LargeTooltip>
          );
        }
      },
      {
        field: 'units_ordered',
        type: 'number',
        headerName: 'Units Ordered',
        headerAlign: 'center',
        align: 'center',
        width: 100,
        sortable: true,
        renderCell: (params) => {
          return params.value === 0 ? '–' : params.value;
        }
      },
      {
        field: 'units_received',
        type: 'number',
        headerName: 'Units Received',
        headerAlign: 'center',
        align: 'center',
        width: 100,
        sortable: true,
        renderCell: (params) => {
          return RECEIVED_STATUSES.includes(params.row.status) ? params.value : '–';
        }
      },
      {
        field: 'assigned_to',
        headerName: 'Assigned To',
        width: 100,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
          return params.row.assigned_to.role
        }
      },
      {
        field: 'days_passed',
        headerName: 'Days Assigned',
        width: 100,
        headerAlign: 'center',
        align: 'center'
      },
      devAction && { field: 'dev_actions', headerName: 'Dev Actions', width: 100, sortable: false, headerAlign: 'center', align: 'center',
        renderCell: (params) => {
          if (!params.row.actions.includes('change_status')) {
            return null;
          }
          return (
            <DevActionRowButtons
              row={params.row} addSnackbar={addSnackbar} fetchPos={fetchPos}
            />
          )
        }
      },
    ].filter(Boolean).map((col) => ({
      ...col,
      disableColumnMenu: true,
    }));
  }, [addSnackbar, fetchPos, devAction, showActions, searchText]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {PoHeader}
      <PoFilters
        dateRangeStart={dateRangeStart}
        setDateRangeStart={setDateRangeStart}
        dateRangeEnd={dateRangeEnd}
        setDateRangeEnd={setDateRangeEnd}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        assignedToFilter={assignedToFilter}
        setAssignedToFilter={setAssignedToFilter}
        typeFilter={typeFilter}
        setTypeFilter={setTypeFilter}
        daysMoreThan={daysMoreThan}
        setDaysMoreThan={setDaysMoreThan}
        daysLessThan={daysLessThan}
        setDaysLessThan={setDaysLessThan}
        flaggedOnly={flaggedOnly}
        setFlaggedOnly={setFlaggedOnly}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        poRows={poRows}
      />
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <DataTable rows={filteredPoRows} columns={poColumns} />
      </Box>
    </Box>
  );
};

export default PurchaseOrders;
